import { Cake, CalendarHeart, Heart } from "lucide-react";
import { Link } from "react-router-dom";

import type { DashboardDateItem } from "@/api/dashboard";
import { Card } from "@/components/ui/card";

type Props = {
  milestones?: DashboardDateItem[];
};

function getDaysUntil(dateValue: string) {
  const today = new Date();
  const target = new Date(dateValue);

  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  return Math.max(
    0,
    Math.round((target.getTime() - today.getTime()) / 86_400_000)
  );
}

function toOrdinal(value: number) {
  const remainder100 = value % 100;

  if (remainder100 >= 11 && remainder100 <= 13) {
    return `${value}th`;
  }

  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
}

export function MilestonesCard({ milestones }: Props) {
  const items = milestones ?? [];

  return (
    <Card className="h-full overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-800 px-6 py-5">
        <h2 className="text-xl font-semibold text-white">
          Upcoming Celebrations
        </h2>
      </div>

      <div className="max-h-[320px] divide-y divide-slate-800 overflow-y-auto">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-400">
            No upcoming milestones.
          </div>
        ) : (
          items.map((item) => {
            const birthday = item.type === "BIRTHDAY";
            const daysUntil = getDaysUntil(item.date);

            return (
              <Link
                key={`${item.type}-${item.member_id ?? item.household_id}-${item.date}`}
                to={
                    item.type === "ANNIVERSARY"
                        ? `/households/${item.household_id}`
                        : `/members/${item.member_id}`
                    }
                className="flex items-center justify-between gap-4 px-5 py-4 transition hover:bg-slate-800/50"
              >
                <div className="flex min-w-0 items-center gap-4">
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                      birthday
                        ? "bg-violet-500/15 text-violet-400"
                        : "bg-rose-500/15 text-rose-400"
                    }`}
                  >
                    {birthday ? (
                      <Cake className="h-5 w-5" />
                    ) : (
                      <Heart className="h-5 w-5" />
                    )}
                  </div>

                  <div>
                    <p className="font-semibold text-white">
                      {item.display_name}
                    </p>
                    <p className="mt-1 text-sm text-slate-400">
                        {birthday
                            ? "Birthday"
                            : item.anniversary_years != null
                                ? `${toOrdinal(item.anniversary_years)} Wedding Anniversary`
                                : "Wedding Anniversary"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-right">
                  <div>
                    <p className="text-sm font-medium text-white">
                      {new Date(item.date).toLocaleDateString([], {
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {daysUntil === 0
                        ? "Today"
                        : `in ${daysUntil} day${daysUntil === 1 ? "" : "s"}`}
                    </p>
                  </div>

                  <CalendarHeart className="h-4 w-4 text-slate-500" />
                </div>
              </Link>
            );
          })
        )}
      </div>
    </Card>
  );
}

